/**
 * This is starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import styled from 'styled-components';

import { SYS_BREAKPOINT_LG } from '@oneaudi/unified-web-common';

import {
  useServicesContext,
  FilterContextProvider,
  cleanupFilterURLHash,
  entryFilters,
  getRangeScope,
  fetchCampaignsForPLP,
  RangeScope,
  FilterResponse,
  useDesktopOrMobileView,
  SeoFilterValue,
  OneCMSLayoutWrapper,
  hasFilterHashMatch,
  useClientServerUtils,
  MandatoryAreaSearchProvider,
  Campaign,
} from '@oneaudi/vtp-shared';

import Toolbar from './components/toolbar/Toolbar';
import TilesWrapper from './components/Tiles/TilesWrapper';

import { QuickFilters } from './components/quickFilters/subcomponents/QuickFilters';
import { ToolBarSkeleton } from './components/Tiles/Skeleton/ToolBarSkeleton';
import { TilesSkeleton } from './components/Tiles/Skeleton/TilesSkeleton';
import { QuickFilterSkeleton } from './components/Tiles/Skeleton/QuickFilterSkeleton';

import { FeatureServiceDependencies } from './FeatureHubAppDefinition';
import { FiltersAndTilesWrapper } from '../FeatureApp.styles';

import { OneCMSContent } from './types/oneCMS';

interface FeatureAppProps {
  seoFilters: SeoFilterValue[];
}

const FeatureApp: React.FC<FeatureAppProps> = ({ seoFilters }: FeatureAppProps) => {
  if (typeof window === 'undefined') {
    return (
      <OneCMSLayoutWrapper useUnifiedLibrary>
        <ToolBarSkeleton />
        <FiltersAndTilesWrapper>
          <QuickFilterSkeleton />
          <TilesSkeleton />
        </FiltersAndTilesWrapper>
      </OneCMSLayoutWrapper>
    );
  }

  const { getConfiguration, getEnvironmentConfig, featureAppConfig, getFeatureServices } =
    useServicesContext();
  const {
    // 'vtp-configuration-service': configurationService,
    'audi-stockcars-store-service': storeService,
    'audi-content-service': contentService,
  } = getFeatureServices<FeatureServiceDependencies>();

  const { quickFilters } = contentService.getContent().fields as OneCMSContent;
  const { filterHandlerOnly, appContext } = contentService?.getContent()?.fields as OneCMSContent;

  const { filterHandlerOnly: filterHandlerOnlyConfig } = featureAppConfig as any;
  const dispatch = useDispatch();
  const isDesktop: boolean = useDesktopOrMobileView(SYS_BREAKPOINT_LG) === 'desktop';

  const vtpConfiguration = getConfiguration();

  const envConfig = getEnvironmentConfig();

  const [filterData, setFilterData] = useState<FilterResponse | undefined>(undefined);
  const [rangeScope, setRangeScope] = useState<RangeScope[]>();
  const [campaignScope, setCampaignScope] = useState<Campaign[]>();

  const { isClient } = useClientServerUtils();

  useEffect(() => {
    const quickFiltersArray: any = [];

    if (quickFilters?.fields) {
      Object.values(quickFilters.fields).forEach((filterNameType: any) => {
        if (filterNameType !== '') {
          const lastIndex = filterNameType.lastIndexOf('.');
          const filterName = filterNameType.substring(0, lastIndex);
          const filterType = filterNameType.substring(lastIndex + 1);
          const filterObject = { filter: filterName, filterType };
          quickFiltersArray.push(filterObject);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!vtpConfiguration || !envConfig) {
      return undefined;
    }

    const fetchFilters = async () => {
      const filterResponse = await entryFilters(
        storeService.store,
        contentService,
        dispatch,
        vtpConfiguration,
        envConfig,
        seoFilters
      );
      setFilterData(filterResponse!);
      cleanupFilterURLHash();
    };

    if (!filterData || hasFilterHashMatch()) {
      fetchFilters().then();
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', fetchFilters);
      return () => window.removeEventListener('hashchange', fetchFilters);
    }
    return undefined;
  }, [filterData]);

  useEffect(() => {
    const fetchRangeScope = async () => {
      const rangeScopeArray = await getRangeScope(
        envConfig!.scs.baseUrl,
        vtpConfiguration!.scs!.scsMarketPath!,
        envConfig!.scs.defaultApiVersion,
        envConfig!.scs.apiKey
      );
      setRangeScope(rangeScopeArray);
    };
    fetchRangeScope().then();
  }, []);

  useEffect(() => {
    const fetchCampaignScope = async () => {
      const campaignScopeArray = await fetchCampaignsForPLP(
        envConfig!.scs.baseUrl,
        vtpConfiguration!.scs!.scsMarketPath!,
        envConfig!.scs.defaultApiVersion,
        envConfig!.scs.apiKey
      );

      setCampaignScope(campaignScopeArray);
    };
    fetchCampaignScope().then();
  }, []);

  if (!vtpConfiguration || !envConfig) {
    return null;
  }
  if (!filterData || filterHandlerOnly || filterHandlerOnlyConfig) {
    return null;
  }

  return (
    <OneCMSLayoutWrapper useUnifiedLibrary>
      <FilterContextProvider
        rangeScopes={rangeScope}
        initialFilterData={filterData}
        campaignData={campaignScope}
        isPlp
      >
        <MandatoryAreaSearchProvider>
          {isClient ? <Toolbar /> : <ToolBarSkeleton />}
          <FiltersAndTilesWrapper>
            {appContext === 'results' && isDesktop && <QuickFilters />}
            <TilesWrapper />
          </FiltersAndTilesWrapper>
        </MandatoryAreaSearchProvider>
      </FilterContextProvider>
    </OneCMSLayoutWrapper>
  );
};

export default FeatureApp;

export const Layout = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
`;
